<script setup lang="ts">
import {
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
  type PopoverArrowProps,
  type PopoverContentProps,
  type PopoverTriggerProps,
} from 'radix-vue'
import { ref } from 'vue'

import { useOutsideOverlay } from '../../composables'
import { injectAppRootElementSelector } from '../../provide-inject'
import { getScaledPx } from '../../utils'
import Icon from '../Icon/Icon.vue'
import TransitionFadeIn from '../transitions/TransitionFadeIn.vue'

type Props = {
  withCloseIcon?: boolean
  withArrow?: boolean
  unstyled?: boolean
  arrow?: PopoverArrowProps & { class?: string }
  trigger?: PopoverTriggerProps & { class?: string }
  content?: PopoverContentProps & { class?: string }
  /**
   * Keeps the popover content hidden when closed, instead of unmounting it.
   *
   * Use this if you need to preserve the internal state of components within the popover.
   *
   * Note: `true` disables enter animations when the popover is reopened.
   */
  isContentHidden?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  withCloseIcon: true,
  withArrow: true,
})

// inject
const appRootElementSelector = injectAppRootElementSelector()

// model
const isPopoverOpen = defineModel<boolean>({ default: false })

// state
const { hasOverlayElement, overlayElementId } = useOutsideOverlay()

/**
 * prevent destroying components in content when popover is closed
 */
const keepContentMounted = ref(false)

// methods
function updateOpen(value: boolean): void {
  isPopoverOpen.value = value

  if (props.isContentHidden) {
    // when content is hidden, subsequent opening of popover would cause 'flickering' of animation,
    // so only first animation will be played
    keepContentMounted.value = !isPopoverOpen.value
  }
}

function onPointerDownOutside(event: Event): void {
  if (isPopoverOpen.value) {
    // allows to properly handle event like 'blur' on input elements inside popover
    // otherwise, destroyed components would not be able to update their values after 'blur'
    event.preventDefault()

    // normally, without above event.preventDefault(), the popover would close on click outside - we do it manually
    isPopoverOpen.value = false
  }
}
</script>

<template>
  <PopoverRoot
    :open="isPopoverOpen"
    @update:open="updateOpen"
  >
    <PopoverTrigger
      v-bind="trigger"
      :class="{
        'cursor-pointer': !isPopoverOpen,
      }"
    >
      <slot name="trigger" />
    </PopoverTrigger>
    <PopoverPortal :to="appRootElementSelector || undefined">
      <!-- NOTE: hardcoded value 10px in reusable component -->
      <PopoverContent
        v-bind="content"
        :class="{
          hidden: !isPopoverOpen,
          'bg-neutral-light-9 shadow-drop-shadow rounded-2xl p-4': !unstyled,
        }"
        :side="content && content.side ? content.side : 'bottom'"
        :sideOffset="withArrow ? undefined : getScaledPx(10)"
        :collisionPadding="getScaledPx(10)"
        @pointerDownOutside="onPointerDownOutside"
      >
        <!--
        needed for proper content transition with vue:
        isPopoverOpen: true if popover is opened, passed to v-if
        forceMount on <PopoverContent> to mount the content even if it's hidden
        -->
        <TransitionFadeIn>
          <div
            v-if="isPopoverOpen || keepContentMounted"
            class="relative"
          >
            <PopoverClose
              v-if="withCloseIcon"
              class="hover:text-neutral-2 text-neutral-dark-0 absolute top-0 right-0 cursor-pointer"
            >
              <Icon icon="close" />
            </PopoverClose>
            <slot name="content" />
          </div>
        </TransitionFadeIn>
        <PopoverArrow
          v-if="withArrow"
          v-bind="arrow"
          class="fill-neutral-light-9 stroke-neutral-dark-10/10 h-4 w-3"
        />
      </PopoverContent>
    </PopoverPortal>
    <Teleport
      v-if="hasOverlayElement && isPopoverOpen"
      :to="`#${overlayElementId}`"
    >
      <!-- layer which disallows accidental click on UI controls with lower z-index -->
      <div class="fixed z-10 h-full w-full" />

      <!--
        NOTE: custom <Teleport> is used because:
        <PopoverContent disableOutsidePointerEvents> doesnt work as expected
        <PopoupRoot modal> works partially - disables click outside popover, but doesn't allow to scroll <body>
        -->
    </Teleport>
  </PopoverRoot>
</template>

<style scoped>
[data-radix-popper-content-wrapper] {
  @apply z-20!;
}
</style>
